import { useState, useCallback, useEffect, useRef } from "react";
import type { Channel as StreamChannel } from "stream-chat";
import { useChatContext } from "stream-chat-react";
import { useParams } from "react-router-dom";
import { useNavigate } from "Navigation/useNavigate";
import { appUrl } from "Utils/UrlUtils";

type Props = {
  isMobile: boolean;
  onDetailViewChange: (isDetail: boolean) => void;
};

export const useChatChannelSync = ({ isMobile, onDetailViewChange }: Props) => {
  const { channelId } = useParams<{ channelId: string }>();
  const navigate = useNavigate();
  const {
    client,
    channel: contextChannel,
    setActiveChannel,
  } = useChatContext();

  const [isChannelOpen, setIsChannelOpen] = useState(!!channelId);
  const lastSyncedContextIdRef = useRef<string | undefined>(undefined);
  const contextChannelIdRef = useRef(contextChannel?.id);
  contextChannelIdRef.current = contextChannel?.id;

  // Resolve channel from URL when it doesn't match context (deep links, browser navigation)
  useEffect(() => {
    if (!channelId || !client) {
      return;
    }
    if (contextChannelIdRef.current === channelId) {
      return;
    }
    let cancelled = false;
    client
      .queryChannels(
        {
          type: "messaging",
          id: channelId,
          members: { $in: [client.userID ?? ""] },
        },
        {},
        { limit: 1 },
      )
      .then((channels) => {
        if (cancelled) {
          return;
        }
        if (channels.length > 0) {
          setActiveChannel(channels[0]);
        } else {
          navigate(appUrl("chat"), { replace: true });
        }
      })
      .catch(() => {
        if (!cancelled) {
          navigate(appUrl("chat"), { replace: true });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [channelId, client, setActiveChannel, navigate]);

  // Sync URL and isChannelOpen when context channel changes (user clicked in list)
  useEffect(() => {
    const contextId = contextChannel?.id;
    if (!contextId) {
      lastSyncedContextIdRef.current = undefined;
      return;
    }
    if (contextId === lastSyncedContextIdRef.current) {
      return;
    }
    lastSyncedContextIdRef.current = contextId;
    setIsChannelOpen(true);
    if (contextId !== channelId) {
      navigate(`/chat/${contextId}`, { replace: true });
    }
  }, [contextChannel?.id, channelId, navigate]);

  // Sync isChannelOpen with URL (for browser back/forward, deep links)
  useEffect(() => {
    setIsChannelOpen(!!channelId);
  }, [channelId]);

  // Notify parent of detail view changes on mobile
  useEffect(() => {
    if (isMobile) {
      onDetailViewChange(isChannelOpen);
    }
  }, [isMobile, isChannelOpen, onDetailViewChange]);

  const activeChannel = (contextChannel as StreamChannel) ?? null;

  const handleBackToList = useCallback(() => {
    setIsChannelOpen(false);
    setActiveChannel(undefined);
    navigate(appUrl("chat"), { replace: true });
  }, [setActiveChannel, navigate]);

  return { activeChannel, isChannelOpen, handleBackToList, setActiveChannel };
};
